import React from 'react'
import { Card, CardContent, Box, Typography, Chip, Skeleton } from '@mui/material'
import {
  CalendarMonthOutlined as CalendarIcon,
  CallOutlined as PhoneIcon,
  AccessTimeOutlined as ClockIcon,
  PersonOutlineOutlined as PersonIcon,
  MedicalServicesOutlined as ServiceIcon,
} from '@mui/icons-material'
import { format, parseISO } from 'date-fns'
import { SMBDashboardSummary } from '../../../services/api'

interface UpcomingAppointmentsListProps {
  summary: SMBDashboardSummary | null;
  summaryLoading: boolean;
}

export const UpcomingAppointmentsList: React.FC<UpcomingAppointmentsListProps> = ({ summary, summaryLoading }) => {
  return (
    <Card sx={{ mb: 4, borderRadius: '16px', border: '1px solid', borderColor: '#F1F5F9', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
      <CardContent sx={{ p: '24px !important' }}>
        <Typography variant="h6" fontWeight="bold" sx={{ letterSpacing: '-0.5px', mb: 3, color: '#0F172A' }}>
          Upcoming Appointments
        </Typography>
        {summaryLoading && !summary ? (
          <Skeleton variant="rectangular" height={100} />
        ) : !summary?.upcoming_appointments?.length ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CalendarIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              No upcoming appointments.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {summary.upcoming_appointments.map((appt, idx) => {
              const startObj = appt.slot_start ? parseISO(appt.slot_start) : null
              const endObj = appt.slot_end ? parseISO(appt.slot_end) : null
              const month = startObj ? format(startObj, 'MMM') : '—'
              const day = startObj ? format(startObj, 'd') : '—'
              const startTimeStr = startObj ? format(startObj, 'h:mm a') : '—'
              const endTimeStr = endObj ? format(endObj, 'h:mm a') : ''
              const timeStr = endTimeStr ? `${startTimeStr} - ${endTimeStr}` : startTimeStr
              
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    py: 2,
                    borderBottom: idx < summary.upcoming_appointments.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 }, flex: 1, minWidth: 0 }}>
                    {/* Calendar tear-off style date block */}
                    <Box sx={{ bgcolor: '#eff6ff', color: '#2563eb', borderRadius: 1.5, py: 1, px: 1.25, minWidth: 48, textAlign: 'center', flexShrink: 0, mt: 0.25 }}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {month}
                      </Typography>
                      <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, lineHeight: 1 }}>
                        {day}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <PersonIcon sx={{ fontSize: '1rem', color: '#475569', flexShrink: 0 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {appt.user_name || appt.user_phone}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5, columnGap: 1.5 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#64748B' }}>
                          <ClockIcon sx={{ fontSize: '0.85rem', color: '#64748B', flexShrink: 0, transform: 'translateY(-1px)' }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.85rem' }, lineHeight: 1.3 }}>
                            {timeStr}
                          </Typography>
                        </Box>
                        {appt.user_phone ? (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#64748B' }}>
                            <PhoneIcon sx={{ fontSize: '0.85rem', color: '#64748B', flexShrink: 0, transform: 'translateY(-0.5px)' }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.85rem' }, lineHeight: 1.3 }}>
                              {appt.user_phone.startsWith('+') ? appt.user_phone : `+${appt.user_phone}`}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                      {appt.services?.length ? (
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
                          <ServiceIcon sx={{ fontSize: '0.85rem', color: '#64748B', flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1.3 }}>
                            {appt.services.join(', ')}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, mt: 0.25 }}>
                    {appt.services?.length ? (
                      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                        <ServiceIcon sx={{ fontSize: '0.85rem', color: '#64748B', flexShrink: 0, mb: 0.25 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {appt.services.join(', ')}
                        </Typography>
                      </Box>
                    ) : null}
                    <Chip label="Confirmed" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: '0.75rem', borderRadius: 1 }} />
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
